import { CaretDown } from '@phosphor-icons/react';
import { PopoverTrigger } from '~/components/ui/popover';
import { Tip } from '~/components/ui/tip';
import { cn } from '~/lib/utils';
import { isAllQuiet, type BarCounts } from '../state-mapping';
import { STATE_COLOR_CLASS, STATE_PILL_LABEL } from '../state-style';
import { summarizeCounts } from '../status-cluster';

/**
 * FRONT-3223: the status cluster as on `origin/main`, for the
 * `eddy.ui-refactoring` OFF path. Two things differ from the reworked one
 * (`../status-cluster.tsx`): the trigger keeps its hairline border, and the
 * quiet state is the words "All quiet", not an icon. The accessible name
 * (`summarizeCounts`) and the dot order are shared, so the two never name a
 * state two ways.
 */

/** Active states first, then the green done tail. */
const DOT_ORDER: { key: keyof BarCounts; label: string; blink?: boolean }[] = [
  { key: 'running', label: STATE_PILL_LABEL.running },
  { key: 'asking', label: STATE_PILL_LABEL.asking, blink: true },
  { key: 'stalled', label: STATE_PILL_LABEL.stalled },
  { key: 'ready', label: STATE_PILL_LABEL.ready },
];

/** One 7px dot + mono count per non-zero state. "All quiet" replaces the
 *  dots entirely when every chat is read and grey, never a row of zeros. */
function StatusDots({ counts }: { counts: BarCounts }) {
  if (isAllQuiet(counts)) {
    return <span className="text-[13px] text-[var(--ink-2)]">All quiet</span>;
  }
  return (
    <span className="inline-flex items-center gap-[9px]">
      {DOT_ORDER.filter((d) => counts[d.key] > 0).map((d) => (
        <Tip key={d.key} label={`${counts[d.key]} ${d.label}`}>
          <span className="inline-flex items-center gap-[5px]" data-testid={`topbar-count-${d.key}`}>
            <span
              className={cn(
                'size-[7px] shrink-0 rounded-full bg-current',
                STATE_COLOR_CLASS[d.key],
                d.blink && 'eddy-tb-blink',
              )}
            />
            <span className="font-mono text-xs font-medium text-[var(--ink-1)]">{counts[d.key]}</span>
          </span>
        </Tip>
      ))}
    </span>
  );
}

const BAR_HEIGHTS = [6, 9, 12, 6];

/** The 4-bar equaliser — only while something is genuinely running. */
function LiveMeter({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <span
      className="ml-px inline-flex h-[14px] items-end gap-[2px] border-l border-[var(--line)] pl-[8px]"
      aria-hidden="true"
    >
      {BAR_HEIGHTS.map((h, i) => (
        <span
          key={i}
          className="eddy-tb-bar w-[2.5px] rounded-[1px] bg-[var(--eddy-blue)] opacity-85"
          style={{ height: h }}
        />
      ))}
    </span>
  );
}

/**
 * The status cluster button — StatusDots + LiveMeter + a caret that rotates
 * when the activity board is open. Renders as the `Popover`'s trigger, so it
 * must be mounted inside a `<Popover>`.
 */
export function StatusClusterLegacy({
  counts,
  className,
  size = 'default',
}: {
  counts: BarCounts;
  className?: string;
  /** FRONT-1579: `sm` fits the chat window's tighter eyebrow header row. */
  size?: 'default' | 'sm';
}) {
  return (
    <PopoverTrigger
      aria-label={`Chat activity: ${summarizeCounts(counts)}`}
      className={cn(
        'group inline-flex shrink-0 items-center gap-[10px] rounded-[11px] border border-[var(--line)]',
        'pr-[11px] pl-[12px] whitespace-nowrap transition-colors',
        'data-[state=open]:border-[var(--line-strong)] data-[state=open]:bg-[var(--bg-3)]',
        size === 'sm' ? 'h-[30px]' : 'h-[38px]',
        className,
      )}
    >
      <StatusDots counts={counts} />
      <LiveMeter active={counts.running > 0} />
      <CaretDown
        size={11}
        className="text-[var(--ink-3)] transition-transform duration-150 group-data-[state=open]:rotate-180"
      />
    </PopoverTrigger>
  );
}
